import React, { useEffect, useState } from "react";
import { Button, Form, InputGroup, Row, Col, Table, Badge, Placeholder } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrash, faMagnifyingGlass, faExternalLink } from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import Pagination from "../Common/Pagination";

const LIMIT = 20;

const ProductsList = ({ workspaceId, categories = [], reload, refresh }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [source, setSource] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const query = { workspace: workspaceId, page, limit: LIMIT };
        if (search) query.search = search;
        if (category) query.category = category;
        if (source) query.source = source;
        const res = await Utils._apiRequest({
          path: Enums.endpoints.products.LIST,
          type: "GET",
          query,
        });
        setItems(res.data || []);
        setCount(res.count || 0);
      } catch (err) {
        console.log("Failed to load products:", err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [workspaceId, page, search, category, source, reload]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Xoá sản phẩm này?")) return;
    try {
      const res = await Utils._apiRequest({
        path: `${Enums.endpoints.products.LIST}/${id}`,
        type: "DELETE",
      });
      if (res.errors) throw new Error(res.errors);
      toast.success("Đã xoá");
      refresh && refresh();
    } catch (err) {
      toast.error(err.message || "Lỗi khi xoá");
    }
  };

  const formatPrice = (p, currency = "USD") =>
    p != null ? `${Number(p).toFixed(2)} ${currency}` : "--";

  return (
    <>
      <Row className="g-2 mb-3 align-items-end">
        <Col md={5}>
          <Form onSubmit={handleSearchSubmit}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Tìm theo tên, SKU, tag..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <Button variant="outline-secondary" type="submit">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </Button>
            </InputGroup>
          </Form>
        </Col>
        <Col md={4}>
          <Form.Select
            value={category}
            onChange={(e) => {
              setPage(1);
              setCategory(e.target.value);
            }}
          >
            <option value="">Tất cả category</option>
            <option value="none">-- Không có category --</option>
            {categories.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name} ({c.productCount || 0})
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Select
            value={source}
            onChange={(e) => {
              setPage(1);
              setSource(e.target.value);
            }}
          >
            <option value="">Tất cả nguồn</option>
            <option value="etsy">Etsy</option>
          </Form.Select>
        </Col>
      </Row>

      <Table striped hover responsive className="products-table">
        <thead>
          <tr>
            <th style={{ width: 20 }}>#</th>
            <th style={{ width: 60 }}>Ảnh</th>
            <th>Tên sản phẩm</th>
            <th style={{ width: 100 }}>Nguồn</th>
            <th style={{ width: 130 }}>Category</th>
            <th style={{ width: 110 }}>Giá</th>
            <th style={{ width: 90 }}>Stock</th>
            <th style={{ width: 150 }} className="text-end">Thao tác</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            [...Array(5)].map((_, i) => (
              <tr key={i}>
                {[...Array(8)].map((__, j) => (
                  <td key={j}>
                    <Placeholder as="span" animation="glow"><Placeholder xs={8} /></Placeholder>
                  </td>
                ))}
              </tr>
            ))
          ) : items.length === 0 ? (
            <tr>
              <td colSpan={8} className="text-center text-muted py-4">
                Chưa có sản phẩm nào.
              </td>
            </tr>
          ) : (
            items.map((p, idx) => (
              <tr key={p._id}>
                <td>{(page - 1) * LIMIT + idx + 1}</td>
                <td>
                  {p.images?.[0]?.url ? (
                    <img
                      src={p.images[0].url}
                      alt=""
                      className="products-table__thumb"
                    />
                  ) : (
                    <div className="products-table__thumb" />
                  )}
                </td>
                <td>
                  <div className="products-table__title">{p.title}</div>
                  {p.sku && <small className="text-muted">SKU: {p.sku}</small>}
                </td>
                <td>
                  <Badge bg="info" className="products-table__source-badge">
                    {p.source}
                  </Badge>
                </td>
                <td>{p.category?.name || <span className="text-muted">--</span>}</td>
                <td>{formatPrice(p.price, p.currency)}</td>
                <td>
                  {p.stockStatus === "outofstock" ? (
                    <Badge bg="secondary">Hết hàng</Badge>
                  ) : (
                    <Badge bg="success">{p.stock ?? 0}</Badge>
                  )}
                </td>
                <td className="text-end">
                  {p.sourceUrl && (
                    <a
                      href={p.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm btn-outline-secondary me-1"
                      title="Mở trang gốc"
                    >
                      <FontAwesomeIcon icon={faExternalLink} />
                    </a>
                  )}
                  <Link
                    to={`/products/${p._id}`}
                    className="btn btn-sm btn-outline-primary me-1"
                    title="Chi tiết"
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                  </Link>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(p._id)}
                    title="Xoá"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <Pagination page={page} setPage={setPage} count={count} limit={LIMIT} />
    </>
  );
};

export default ProductsList;
